import { logToAi } from './logging';
import { ansiReset, bB, buT, cT, gT, pT, rT, yT } from './logStyling';

const logError = (url, method, retriesRemaining, error, statusCode) => {
	let stringifiedError = '';
	try {
		if (typeof error === 'function')
			console.warn(
				`${rT}How on earth was the error object a ${buT}function${rT}?${ansiReset}`
			);
		if (typeof error === 'object')
			stringifiedError = JSON.stringify(
				error,
				Object.getOwnPropertyNames(error)
			);
		else stringifiedError = error;
	} catch (err) {
		console.debug(
			`${rT}ERROR${yT}: Couldn't stringify fetchCall error${ansiReset}`,
			err,
			err?.message
		);
		stringifiedError =
			'Error could not be stringified.' +
			' This is OK so long as we got most of the important data.';
	}
	logToAi(
		{
			message: `API call error!`,
			id: 'API_FETCH_CALL_UNEXPECTED_ERROR',
			location: 'fetchUtils.js:fetchCall',
			parameters: {
				url: url,
				method: method,
				retriesRemaining: retriesRemaining,
			},
			data: {
				error: {
					message:
						error?.message ??
						error?.response?.message ??
						error?.toString() ??
						'No error message found.',
					data:
						error?.data ??
						error?.response?.data ??
						'No data found.',
					stack:
						error?.stack ??
						error?.response?.stack ??
						'No stack found.',
					stringified: stringifiedError,
				},
				statusCode: statusCode,
			},
		},
		'error'
	);
};

/**
 * This makes the call to your Next.JS API. It takes the url that we pass in and
 * forwards that to the NEXT.JS API so that it can be added on to the respective cumulus services
 * domain. Then it takes the response and does the error handling in one place so that we can just use this one
 * call and then use `.then` to get the response and `.catch` to handle the error appropriately
 *
 * @param {string} url The url that will be appended to the domain name to route the calls to the next js api folder.
 * @param {('GET' | 'POST' | 'PUT' | 'DELETE' | 'OPTIONS')} method **DEFAULT**: `"GET"` - A valid HTTP method.
 * @param {*} [body] The request body
 * @param {*} [header] The request headers
 * @param {number} [retriesRemaining] **DEFAULT**: `3` - This is the number of retries the API will attempt
 * if it fails with a 500 server error.
 * @param {number} [baseRetryTimer] **DEFAULT**: `1000` - The initial amount of time to wait between retries.
 *
 * This will have an square exponential backoff timer; so if the base is 1000 for 3 retries
 * it will take 1000ms for the first retry, 2000ms for the second, and 4000ms for the third.
 * @returns A `Promise` that resolves to the response from the API call or rejects with an error if the call fails
 */
export const fetchCall = (
	url,
	method,
	body,
	header = {},
	retriesRemaining = 3,
	baseRetryTimer = 1000
) => {
	logToAi(
		{
			message: `In fetchCall for ${url}!`,
			id: 'API_FETCH_CALL_TEMP',
			location: 'fetchUtils.js:fetchCall',
			parameters: {
				url: url,
				method: method,
				retriesRemaining: retriesRemaining,
			},
		},
		'debug'
	);
	// logError(url, method, retriesRemaining, {}, 0);

	return new Promise((resolve, reject) => {
		/** The status code of the fetch call.  Useful for logging in the .catch() block*/
		let statusCode = 500;
		let options = {};
		try {
			options = {
				method,
				headers: {
					Accept: 'application/json',
					...header,
				},
			};
			if (method !== 'GET') {
				// console.log('isfile', body instanceof FormData);
				if (body instanceof File || body instanceof FormData) {
					console.log('body is body');
					options.body = body;
				} else {
					options.body = JSON.stringify(body);
					options.headers['Content-Type'] = 'application/json';
				}
			}
		} catch (e) {
			logError(url, method, retriesRemaining, e, statusCode);

			console.error('fetchCall - Unexpected error parsing options', e);
			return reject(e);
		}

		fetch(url, options)
			.then((response) => {
				statusCode =
					response?.status ??
					`response.status was undefined for fetch(${url})`;

				// Handle error responses
				if (response.status >= 400 || response.status < 200) {
					if (
						(response.status >= 500 || response.status === 408) &&
						retriesRemaining > 0
					) {
						console.debug(
							`${pT}Retrying ${buT}${url}${pT}... attempts remaining: ${buT}${
								retriesRemaining - 1
							}${pT}.${ansiReset}`
						);
						return wait(baseRetryTimer).then(() => {
							return fetchCall(
								url,
								method,
								body,
								header,
								retriesRemaining - 1,
								baseRetryTimer * 2
							)
								.then(resolve)
								.catch(reject);
						});
					}
					// Handle 4xx and 5xx responses without retries
					return response.text().then((responseText) => {
						try {
							const errorResponse = JSON.parse(responseText);
							console.log('errorResponse', errorResponse);
							logToAi(
								{
									message: `API responded with statusCode ${response.status} for ${url}!`,
									id: 'API_FETCH_CALL_ERROR',
									location: 'fetchUtils.js:fetchCall',
									parameters: {
										url: url,
										method: method,
										retriesRemaining: retriesRemaining,
									},
									data: {
										statusCode: response.status,
										response: errorResponse,
									},
								},
								'error'
							);

							return reject(errorResponse);
						} catch (err) {
							logToAi(
								{
									message: `API responded with statusCode ${response.status} for ${url}!`,
									id: 'API_FETCH_CALL_ERROR',
									location:
										'fetchUtils.js:fetchCall:catchBlock',
									parameters: {
										url: url,
										method: method,
										retriesRemaining: retriesRemaining,
									},
									data: {
										statusCode: response.status,
										response: responseText,
									},
								},
								'error'
							);

							return reject(responseText);
						}
					});
				}

				// Handle 204 No Content response
				if (response.status === 204) {
					return resolve('');
				}

				if (
					String(response?.headers?.get('content-type')).includes(
						'image'
					)
				) {
					// console.debug(
					// 	`${cT}fetchUtils${ansiReset}:${bB}${gT}IMAGE DOWNLOAD${ansiReset}`
					// );
					// Don't do anything to the response if it's an image
					return resolve(response);
				}
				// Parse response JSON
				return response.json().then((data) => {
					const formattedResponse = toLowerCamelCase(data);
					resolve(formattedResponse);
				});
			})
			.catch((error) => {
				logError(url, method, retriesRemaining, error, statusCode);

				// These 3 messages are specific to Chromium-based browsers, Firefox, and Safari respectively
				if (
					(error.message === 'Failed to fetch' ||
						error.message ===
							'NetworkError when attempting to fetch resource.' ||
						error.message === 'Load failed') &&
					retriesRemaining > 0
				) {
					console.debug(
						`${pT}Retrying ${buT}${url}${pT} due to network failure... attempts remaining: ${buT}${
							retriesRemaining - 1
						}${pT}.${ansiReset}`
					);
					return wait(baseRetryTimer).then(() => {
						return fetchCall(
							url,
							method,
							body,
							header,
							retriesRemaining - 1,
							baseRetryTimer * 2
						)
							.then(resolve)
							.catch(reject);
					});
				} else {
					reject(error);
				}
			});
	});
};

export const serializeGetParams = (params) => {
	const keys = Object.keys(params).filter(
		(key) => typeof params[key] !== 'undefined'
	);

	if (keys.length) {
		return `?${keys
			.map(
				(key) =>
					`${encodeURIComponent(key)}=${encodeURIComponent(
						params[key]
					)}`
			)
			.join('&')}`;
	}

	return '';
};

export const toLowerCamelCase = (object) => {
	if (!(object instanceof Object)) return object;
	if (object instanceof Array) {
		return object.map(toLowerCamelCase);
	}
	const keys = Object.keys(object);
	return keys.length
		? keys.reduce((o, key) => {
				const value = object[key];
				o[key.charAt(0).toLowerCase() + key.slice(1)] =
					value instanceof Object ? toLowerCamelCase(value) : value;
				return o;
			}, {})
		: object;
};

function wait(delay) {
	return new Promise((resolve) => setTimeout(resolve, delay));
}
